import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class MakeItASetDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    // Clean up any existing related sections to prevent duplicates
    this.cleanupRelatedSections();

    // Initialize carousels
    this.initializeCarousels();

    // Initialize pull it all together button
    this.initializePullTogether();

    // Initialize add to cart
    this.initializeAddToCart();

    // Initialize variant selection handling
    this.initializeVariantHandling();

    // Calculate initial total
    this.updateTotalPrice();
  }

  /**
   * Check if "Pull it all together" feature is enabled
   * @returns {boolean}
   */
  shouldShowPullTogether() {
    const setting = this.getAttribute('data-show-pull-together');
    return setting === 'true';
  }

  /**
   * Check if related products should be hidden via metafield
   * @returns {boolean}
   */
  shouldHideRelatedProducts() {
    const setting = this.getAttribute('data-hide-related-products');
    return setting === 'true';
  }

  cleanupRelatedSections() {
    // Remove any existing related sections (no dividers to clean up since we don't create them anymore)
    const existingRelatedSections = this.querySelectorAll('.make-it-a-set-related-section');

    existingRelatedSections.forEach(section => section.remove());

    // Only recreate the pull together button if the setting is enabled
    if (!this.shouldShowPullTogether()) {
      console.log('🔧 Pull together feature disabled, skipping button recreation');
      return;
    }

    // Recreate the pull together button container if it doesn't exist
    const pullTogetherContainer = this.querySelector('.make-it-a-set-pull-together');
    if (!pullTogetherContainer) {
      // Find the complementary section to insert after
      const complementarySection = this.querySelector('.make-it-a-set-complementary-section');
      if (complementarySection) {
        const newPullTogetherContainer = document.createElement('div');
        newPullTogetherContainer.className = 'make-it-a-set-pull-together';
        newPullTogetherContainer.innerHTML = `
          <button type="button" class="make-it-a-set-pull-together__button" id="pull-it-all-together">
            <span>Pull it all together</span>
          </button>
        `;

        complementarySection.insertAdjacentElement('afterend', newPullTogetherContainer);
        console.log('🔧 Recreated pull together button container');
      }
    } else {
      // Reset existing container
      pullTogetherContainer.style.display = 'flex';

      const pullButton = pullTogetherContainer.querySelector('#pull-it-all-together');
      if (pullButton) {
        pullButton.style.display = 'block';
        pullButton.disabled = false;
        pullButton.innerHTML = '<span>Pull it all together</span>';
      }
    }

    console.log('🧹 Cleaned up existing related sections while preserving original divider');
  }

  initializeCarousels() {
    const carousels = this.querySelectorAll('.make-it-a-set-product-carousel');

    carousels.forEach(carousel => {
      this.setupCarousel(carousel);
    });
  }

  setupCarousel(carousel) {
    const track = carousel.querySelector('.make-it-a-set-product-carousel__track');
    const slides = carousel.querySelectorAll('.make-it-a-set-product-carousel__slide');
    // Use custom arrow selectors
    const prevBtn = carousel.querySelector('.make-it-a-set-product-carousel__arrow--prev');
    const nextBtn = carousel.querySelector('.make-it-a-set-product-carousel__arrow--next');
    const counter = carousel.querySelector('.make-it-a-set-product-carousel__counter-current');

    if (!track || slides.length <= 1) return;

    let currentIndex = 0;

    const updateCarousel = () => {
      // Hide all slides
      slides.forEach(slide => {
        slide.classList.remove('make-it-a-set-product-carousel__slide--active');
      });

      // Show current slide
      slides[currentIndex].classList.add('make-it-a-set-product-carousel__slide--active');

      // Update counter
      if (counter) {
        counter.textContent = currentIndex + 1;
      }

      // Update buttons
      if (prevBtn) {
        prevBtn.disabled = currentIndex === 0;
      }
      if (nextBtn) {
        nextBtn.disabled = currentIndex === slides.length - 1;
      }

      // Update total price
      this.updateTotalPrice();
    };

    // Previous button
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (currentIndex > 0) {
          currentIndex--;
          updateCarousel();
        }
      });
    }

    // Next button
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        if (currentIndex < slides.length - 1) {
          currentIndex++;
          updateCarousel();
        }
      });
    }

    // Initial setup
    updateCarousel();
  }

  initializePullTogether() {
    console.log('🔧 Initializing Pull Together button');
    const button = this.querySelector('#pull-it-all-together');

    console.log('🔧 Button found:', !!button);
    if (button) {
      console.log('🔧 Button element:', button);
      console.log('🔧 Button disabled:', button.disabled);
      console.log('🔧 Button display:', button.style.display);
    }

    if (!button) {
      console.error('🔧 No pull together button found!');
      return;
    }

    button.addEventListener('click', async (event) => {
      console.log('🔥 BUTTON CLICKED! Event:', event);

      // Prevent any default behavior and stop propagation
      event.preventDefault();
      event.stopPropagation();

      try {
        console.log('🔥 Pull it all together button clicked - starting process');

        // Show loading state on button
        button.disabled = true;
        button.innerHTML = '<span>Loading...</span>';
        console.log('🔥 Button state changed to loading');

        // Fetch related products section content
        console.log('🔥 About to call fetchAndInsertRelatedSection');
        await this.fetchAndInsertRelatedSection();
        console.log('🔥 fetchAndInsertRelatedSection completed');

        // Remove the entire pull together container to prevent spacing issues
        const pullTogetherContainer = this.querySelector('.make-it-a-set-pull-together');
        if (pullTogetherContainer) {
          pullTogetherContainer.remove();
          console.log('🔥 Pull together container removed completely');
        }

        // Update total price
        this.updateTotalPrice();
        console.log('🔥 Total price updated');

        if (!document.querySelector(".close-pit")) {
          const relatedSection = document.querySelector(".make-it-a-set-related-section");
          if (!relatedSection) return;

          let closePIT = document.createElement("button")
          closePIT.type = "button"
          closePIT.classList.add("close-pit")
          closePIT.setAttribute("aria-label", "Close related products section")
          
          // Use SVG icon instead of text
          closePIT.innerHTML = `
            <span class="svg-wrapper">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          `
          
          relatedSection.appendChild(closePIT)
          
          document.querySelector(".close-pit").addEventListener("click", (event) => {
            // Prevent event from bubbling up and closing the drawer
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            console.log("close PIT")

            // Only recreate the button if the setting is enabled
            if (this.shouldShowPullTogether()) {
              let newPIT = document.createElement("div")
              newPIT.classList.add("make-it-a-set-pull-together")
              newPIT.style.display = "flex"
              newPIT.innerHTML = `<button type="button" class="make-it-a-set-pull-together__button" id="pull-it-all-together" style="display: block;"><span>Pull it all together</span></button>`

              document.querySelector(".make-it-a-set-related-section").after(newPIT)
              document.querySelector("make-it-a-set-drawer-component").initializePullTogether()
            }

            document.querySelector(".make-it-a-set-related-section").remove()
            document.querySelector(".close-pit").classList.add("hidden")
            this.updateTotalPrice();
            
            // Return false to ensure event doesn't propagate
            return false;
          })

        } else {
          document.querySelector(".close-pit").classList.remove("hidden")
        }

        console.log('🔥 Related products section added successfully');

      } catch (error) {
        console.error('🔥 Failed to load related products:', error);
        console.error('🔥 Error stack:', error.stack);

        // Restore button state on error
        button.disabled = false;
        button.innerHTML = '<span>Pull it all together</span>';
        console.log('🔥 Button state restored after error');
      }
    });
  }

  async fetchAndInsertRelatedSection() {
    console.log('🚀 Starting fetchAndInsertRelatedSection - using related products template');

    // Check if related products should be hidden via metafield
    if (this.shouldHideRelatedProducts()) {
      console.log('ℹ️ Related products are hidden via metafield for this product');
      return;
    }

    try {
      // Get the related products template
      const template = this.querySelector('#related-products-template');

      if (!template) {
        console.log('ℹ️ No related products template found, falling back to cloning complementary section');
        this.cloneComplementaryAsRelated();
        return;
      }

      console.log('📋 Found related products template');

      // Clone the template content
      const templateContent = template.content.cloneNode(true);

      // Extract the related section from the template
      const relatedSection = templateContent.querySelector('.make-it-a-set-related-section');

      if (!relatedSection) {
        console.log('⚠️ No related section found in template, falling back to cloning complementary section');
        this.cloneComplementaryAsRelated();
        return;
      }

      console.log('✅ Successfully extracted related products section from template');
      // Insert the section
      this.insertRelatedSection(relatedSection);

      console.log('✅ Successfully inserted related products section');

    } catch (error) {
      console.error('❌ Error in fetchAndInsertRelatedSection:', error);
      console.log('🔄 Falling back to cloning complementary section');
      this.cloneComplementaryAsRelated();
    }
  }

  cloneComplementaryAsRelated() {
    console.log('🔄 Cloning complementary section as fallback');

    // Check if related products should be hidden via metafield
    if (this.shouldHideRelatedProducts()) {
      console.log('ℹ️ Related products are hidden via metafield for this product');
      return;
    }

    const complementarySection = this.querySelector('.make-it-a-set-complementary-section');

    if (!complementarySection) {
      console.error('❌ No complementary section found to duplicate');
      this.insertPlaceholderSection();
      return;
    }

    // Clone the complementary section
    const clonedSection = complementarySection.cloneNode(true);

    // Update the cloned section to be the related section
    clonedSection.classList.remove('make-it-a-set-complementary-section');
    clonedSection.classList.add('make-it-a-set-related-section');

    // Update the carousel to have section type "related"
    const carousel = clonedSection.querySelector('.make-it-a-set-product-carousel');
    if (carousel) {
      carousel.setAttribute('data-section-type', 'related');

      // Add "Related Products" label
      const existingLabel = carousel.querySelector('.make-it-a-set-product-section__label');
      if (existingLabel) {
        existingLabel.textContent = 'Related Products';
      } else {
        // Create label if it doesn't exist
        const label = document.createElement('p');
        label.className = 'make-it-a-set-product-section__label';
        label.textContent = 'Related Products';
        carousel.insertBefore(label, carousel.firstChild);
      }
    }

    // Insert the section
    this.insertRelatedSection(clonedSection);
  }


  insertRelatedSection(relatedSection) {
    // Check if related products should be hidden via metafield
    if (this.shouldHideRelatedProducts()) {
      console.log('ℹ️ Related products are hidden via metafield for this product');
      return;
    }

    // Check if related section already exists to prevent duplicates
    const existingRelatedSection = this.querySelector('.make-it-a-set-related-section');
    if (existingRelatedSection) {
      console.log('⚠️ Related section already exists, skipping insertion');
      return;
    }

    // Find the insertion point (after the complementary products section)
    const complementarySection = this.querySelector('.make-it-a-set-complementary-section');
    if (!complementarySection) {
      throw new Error('No complementary section found for insertion point');
    }

    // Insert the related section directly after complementary section (no divider)
    complementarySection.insertAdjacentElement('afterend', relatedSection);

    console.log('✅ Inserted related section after complementary products section (no divider)');

    // Initialize carousel for the newly added section
    const relatedCarousel = relatedSection.querySelector('.make-it-a-set-product-carousel');
    if (relatedCarousel) {
      console.log('🎠 Initializing carousel for related section');
      this.setupCarousel(relatedCarousel);
    }
  }

  insertPlaceholderSection() {
    const placeholderHTML = `
      <div class="make-it-a-set-product-carousel" data-section-type="related">
        <p>No related products section found to duplicate.</p>
      </div>
    `;

    const complementarySection = this.querySelector('.make-it-a-set-complementary-section');
    if (complementarySection) {
      const relatedSection = document.createElement('div');
      relatedSection.className = 'make-it-a-set-product-section make-it-a-set-related-section';
      relatedSection.innerHTML = placeholderHTML;

      complementarySection.insertAdjacentElement('afterend', relatedSection);
    }
  }

  extractProductIdsFromGids(gidString) {
    const matches = gidString.match(/gid:\/\/shopify\/Product\/(\d+)/g);
    if (!matches) return [];

    return matches.map(match => {
      const id = match.split('/').pop();
      return id;
    });
  }

  async generateRelatedProductsHTML(relatedProducts) {
    if (!relatedProducts || relatedProducts.length === 0) {
      return `
        <div class="make-it-a-set-product-carousel" data-section-type="related">
          <p class="make-it-a-set-product-section__label">Related Products</p>
          <p>No related products found.</p>
        </div>
      `;
    }

    // Generate carousel HTML with navigation
    const hasMultipleProducts = relatedProducts.length > 1;
    const productSlides = relatedProducts.map((product, index) => {
      const isActive = index === 0 ? 'make-it-a-set-product-carousel__slide--active' : '';
      return `
        <div class="make-it-a-set-product-carousel__slide ${isActive}" data-product-index="${index}">
          ${this.generateProductItemHTML(product, false)}
        </div>
      `;
    }).join('');

    const navigationHTML = hasMultipleProducts ? `
      <div class="make-it-a-set-product-carousel__navigation">
        <button class="make-it-a-set-product-carousel__arrow make-it-a-set-product-carousel__arrow--prev" type="button" aria-label="Previous product">
          <span class="svg-wrapper icon-arrow icon-arrow--prev">
            <!-- Arrow SVG will be loaded via CSS -->
          </span>
        </button>
        <button class="make-it-a-set-product-carousel__arrow make-it-a-set-product-carousel__arrow--next" type="button" aria-label="Next product">
          <span class="svg-wrapper icon-arrow icon-arrow--next">
            <!-- Arrow SVG will be loaded via CSS -->
          </span>
        </button>
      </div>
    ` : '';

    return `
      <div class="make-it-a-set-product-carousel" data-section-type="related">
        <p class="make-it-a-set-product-section__label">Related Products</p>
        <div class="make-it-a-set-product-carousel__container">
          ${navigationHTML}
          <div class="make-it-a-set-product-carousel__track" data-current-index="0">
            ${productSlides}
          </div>
        </div>
      </div>
    `;
  }

  generateProductItemHTML(product, isCurrent = false) {
    const selectedVariant = product.selected_or_first_available_variant || product.variants[0];
    const price = selectedVariant ? (selectedVariant.price / 100).toFixed(2) : '0.00';

    // Use the last image from the product images array (matching the pattern from updateProductItemWithNewData)
    let imageUrl = '';
    if (product.images && product.images.length > 0) {
      const lastImageIndex = product.images.length - 1;
      imageUrl = product.images[lastImageIndex];
    } else if (selectedVariant && selectedVariant.featured_image && selectedVariant.featured_image.src) {
      imageUrl = selectedVariant.featured_image.src;
    } else if (product.featured_image) {
      imageUrl = product.featured_image;
    }

    // Process product title and collection label (matching the logic from updateProductItemWithNewData)
    let productTitle = product.title;
    let collectionLabel = '';

    // Extract title before " | " if it exists
    if (productTitle.includes(' | ')) {
      productTitle = productTitle.split(' | ')[0];
    }

    // Get collection label from metafields
    if (product.metafields && product.metafields.custom && product.metafields.custom.collection_label) {
      collectionLabel = product.metafields.custom.collection_label;

      // Remove collection label from title if it exists at the beginning
      if (productTitle.startsWith(collectionLabel + ' ')) {
        productTitle = productTitle.substring(collectionLabel.length + 1);
      } else if (productTitle.startsWith(collectionLabel)) {
        productTitle = productTitle.substring(collectionLabel.length).trim();
      }
    } else {
      // Try to infer collection label from title pattern
      const titleWords = productTitle.split(' ');
      if (titleWords.length >= 3) {
        const potentialLabel = titleWords.slice(0, -1).join(' ');
        collectionLabel = potentialLabel;
        productTitle = titleWords[titleWords.length - 1];
      }
    }

    // Generate variant picker options (simplified for now)
    const variantOptions = this.generateVariantPickerHTML(product, selectedVariant);

    return `
      <div class="make-it-a-set-product-item">
        <div class="make-it-a-set-product-item__image">
          <img src="${imageUrl}?width=600" alt="${productTitle}" loading="lazy">
        </div>
        <div class="make-it-a-set-product-item__details">
          ${collectionLabel ? `<p class="make-it-a-set-product-item__brand">${collectionLabel}</p>` : ''}
          <h3 class="make-it-a-set-product-item__title">${productTitle}</h3>
          <div class="make-it-a-set-product-item__price">$${price}</div>
          ${variantOptions}
        </div>
      </div>
    `;
  }

  generateVariantPickerHTML(product, selectedVariant) {
    if (!product.variants || product.variants.length <= 1) {
      return '';
    }

    // Generate a simplified variant picker (this could be enhanced later)
    const variantPickerId = `variant-picker-${product.id}-${Date.now()}`;

    const colorOptions = this.getUniqueOptionValues(product, 'Color');
    const sizeOptions = this.getUniqueOptionValues(product, 'Size');

    let variantPickerHTML = `<div class="make-it-a-set-variant-picker" data-product-id="${product.id}">`;

    // Color options
    if (colorOptions.length > 1) {
      variantPickerHTML += `
        <div class="variant-option-group">
          <label class="variant-option-label">Color</label>
          <div class="variant-options">
            ${colorOptions.map((color, index) => `
              <label class="variant-option">
                <input type="radio" name="Color-${variantPickerId}" value="${color}" ${index === 0 ? 'checked' : ''}>
                <span class="variant-option-text">${color}</span>
              </label>
            `).join('')}
          </div>
        </div>
      `;
    }

    // Size options
    if (sizeOptions.length > 1) {
      variantPickerHTML += `
        <div class="variant-option-group">
          <label class="variant-option-label">Size</label>
          <div class="variant-options">
            ${sizeOptions.map((size, index) => `
              <label class="variant-option">
                <input type="radio" name="Size-${variantPickerId}" value="${size}" ${index === 0 ? 'checked' : ''}>
                <span class="variant-option-text">${size}</span>
              </label>
            `).join('')}
          </div>
        </div>
      `;
    }

    variantPickerHTML += '</div>';
    return variantPickerHTML;
  }

  getUniqueOptionValues(product, optionName) {
    if (!product.options || !product.variants) return [];

    const optionIndex = product.options.findIndex(opt => opt === optionName || opt.name === optionName);
    if (optionIndex === -1) return [];

    const values = product.variants.map(variant => variant.options[optionIndex]).filter(Boolean);
    return [...new Set(values)]; // Remove duplicates
  }

  initializeAddToCart() {
    const button = this.querySelector('.cart__checkout-button');

    if (!button) return;

    button.addEventListener('click', async () => {
      const variants = this.getSelectedVariants();

      if (variants.length === 0) {
        console.warn('🛒 No variants selected');
        return;
      }

      // Validate all variants have valid IDs
      const invalidVariants = variants.filter(v => !v || !v.id || isNaN(parseInt(v.id)));
      if (invalidVariants.length > 0) {
        console.error('🛒 Invalid variants found:', invalidVariants);
        button.innerHTML = 'Invalid Selection';
        setTimeout(() => {
          button.innerHTML = button.innerHTML.includes('Add Set') ? 'Add Set to Cart' : 'Add to Cart';
        }, 2000);
        return;
      }

      button.disabled = true;
      const originalText = button.innerHTML;
      button.innerHTML = 'Adding to Cart...';

      try {
        // Determine discount information
        const relatedSectionExists = this.querySelector('.make-it-a-set-related-section');
        const discountPercentage = relatedSectionExists ? 15 : 10;
        const isSetPurchase = variants.length > 1;

        // Start fly to cart animation immediately for better UX
        this.triggerFlyToCartAnimation();

        // Add all variants to cart with properties to indicate set purchase
        const cartItems = variants.map(variant => {
          const properties = isSetPurchase ? {
            '_make_it_a_set': 'true',
            '_set_discount_percentage': discountPercentage.toString(),
            '_set_item_count': variants.length.toString()
          } : {};
          
          // Add _finalsale property if variant has compare_at_price > price,
          // or if the product carries the final-sale-active tag.
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            properties['_finalsale'] = 'true';
          } else if (variant._finalSaleTag) {
            properties['_finalsale'] = 'true';
          }
          
          return {
            id: variant.id,
            quantity: 1,
            properties
          };
        });

        console.log('🛒 Preparing to add cart items:', cartItems);

        // Use bulk add to cart
        const response = await this.bulkAddToCart(cartItems);

        console.log('🛒 Cart items added successfully');

        // Animate button after successful addition
        this.animateAddToCart(button);

        // Dispatch cart events for theme integration (don't wait for discount code)
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Fetch cart data to include in event
        fetch('/cart.js')
          .then((cartResponse) => cartResponse.json())
          .then((cart) => {
            this.dispatchEvent(
              new CartAddEvent(cart, variants.map(v => v.id).join(','), {
                source: 'make-it-a-set-drawer',
                itemCount: variants.length,
                productId: variants[0]?.product_id || ''
              })
            );
          })
          .catch(() => {
            // Fallback if cart fetch fails
            this.dispatchEvent(
              new CartAddEvent({}, variants.map(v => v.id).join(','), {
                source: 'make-it-a-set-drawer',
                itemCount: variants.length,
                productId: variants[0]?.product_id || ''
              })
            );
          });

        // Apply discount code asynchronously (don't block UI)
        if (isSetPurchase) {
          this.applyDiscountCode('MAKE-IT-A-SET').catch(err => {
            console.warn('Discount code application failed:', err);
          });
        }

        // Close drawer after user has seen the success feedback
        setTimeout(() => {
          this.close();
          document.querySelector("cart-drawer-component").open()
        }, 1200);

      } catch (error) {
        console.error('🛒 Failed to add to cart:', error);

        // Reset button immediately on error
        button.innerHTML = originalText;
        button.disabled = false;
        button.classList.remove('atc-added');

        // Show error feedback to user
        const errorMessage = error.message || 'Failed to add to cart';
        button.innerHTML = 'Error - Try Again';

        // Reset after showing error
        setTimeout(() => {
          button.innerHTML = originalText;
        }, 2000);

        // Don't close drawer on error
        return;
      }

      // Re-enable button after successful add to cart
      setTimeout(() => {
        button.disabled = false;
      }, 1500);
    });
  }

  // Remove initializeCloseButton - handled by DialogComponent

  initializeVariantHandling() {
    // Listen for variant changes on all variant pickers in the drawer
    this.addEventListener('change', (event) => {
      const input = event.target;

      // Check if this is a variant picker radio input (works with main variant picker)
      if (input.type === 'radio' && input.closest('variant-picker')) {
        console.log('Variant selection changed:', input.name, input.value);

        const picker = input.closest('variant-picker');
        if (picker) {
          // Only prevent bubbling to main page, don't prevent default behavior
          event.stopPropagation();

          // Only handle if the picker is within this drawer
          if (this.contains(picker)) {
            this.handleVariantSelection(picker, input);
          }
        }
      }
    }, true); // Use capture phase to ensure we catch events first
  }

  async handleVariantSelection(picker, selectedInput) {
    if (!selectedInput.checked) return;

    console.log('=== VARIANT SELECTION START ===');
    console.log('Selected input value:', selectedInput.value);
    console.log('Selected input name:', selectedInput.name);
    console.log('Selected input dataset:', selectedInput.dataset);
    console.log('Current picker data-product-url:', picker.getAttribute('data-product-url'));

    // Determine if this is a lightweight variant change that shouldn't trigger re-rendering
    const inputName = selectedInput.name.toLowerCase();
    const inputValue = selectedInput.value.toLowerCase();

    // These variant types should only update price/data, not re-render the entire product
    const lightweightVariantKeywords = [
      'size', 'fit', 'length', 'specification', 'width', 'height',
      'dimension', 'measurement', 'curve', 'straight', 'regular'
    ];

    const isLightweightVariantChange = lightweightVariantKeywords.some(keyword =>
      inputName.includes(keyword) || inputValue.includes(keyword)
    );
    const connectedProductUrl = selectedInput.dataset.connectedProductUrl;
    const currentProductUrl = picker.getAttribute('data-product-url');
    const isProductSwitch = connectedProductUrl && connectedProductUrl !== currentProductUrl;

    console.log('Is lightweight variant change:', isLightweightVariantChange);
    console.log('Input name contains:', inputName);
    console.log('Connected product URL:', connectedProductUrl);
    console.log('Current product URL:', currentProductUrl);
    console.log('Is product switch:', isProductSwitch);

    if (isLightweightVariantChange && !isProductSwitch) {
      // Handle lightweight variant changes without re-rendering the whole item
      console.log('🔧 LIGHTWEIGHT VARIANT CHANGE: Using price-only update');
      await this.handleLightweightVariantChange(picker, selectedInput);
    } else {
      // Handle color changes and product switches with section rendering
      const targetProductUrl = connectedProductUrl || currentProductUrl;
      console.log('🎨 COLOR/PRODUCT CHANGE: Using section rendering');
      console.log('Target product URL:', targetProductUrl);
      await this.fetchAndUpdateProductUnified(picker, selectedInput, targetProductUrl);
    }

    console.log('=== VARIANT SELECTION END ===');
  }

  async handleLightweightVariantChange(picker, selectedInput) {
    try {
      console.log('🔧 LIGHTWEIGHT VARIANT: Update for option:', selectedInput.name, '=', selectedInput.value);

      const productItem = picker.closest('.make-it-a-set-product-item');
      if (!productItem) return;

      // Get current product URL
      const currentProductUrl = picker.getAttribute('data-product-url');
      if (!currentProductUrl) {
        console.error('No product URL found on picker');
        return;
      }

      // Show subtle loading state
      productItem.style.opacity = '0.9';

      // Fetch product JSON to find the new variant
      const jsonUrl = currentProductUrl + '.js';
      const response = await fetch(jsonUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const productData = await response.json();

      // Find the selected variant based on all current selections
      const selectedVariant = this.findSelectedVariantFromAllInputs(picker, productData);
      if (selectedVariant) {
        console.log('🔧 LIGHTWEIGHT VARIANT: Found variant for selection:', selectedVariant);

        // Update only the price and variant-specific data
        this.updatePriceOnly(productItem, selectedVariant);

        // Update the picker's script with new variant data
        const pickerScript = picker.querySelector('script[type="application/json"]');
        if (pickerScript) {
          pickerScript.textContent = JSON.stringify(selectedVariant);
          console.log('🔧 SIZE CHANGE: Updated picker script');
        }

        // Update the hidden variant ID input
        const variantIdInput = productItem.querySelector('.variant-id-input');
        if (variantIdInput) {
          variantIdInput.value = selectedVariant.id;
          console.log('🔧 SIZE CHANGE: Updated variant ID input to:', selectedVariant.id);
        }
      }

      // Restore opacity
      productItem.style.opacity = '1';

      // Update total price
      this.updateTotalPrice();

      console.log('🔧 SIZE CHANGE: Complete');

    } catch (error) {
      console.error('🔧 SIZE CHANGE: Failed:', error);

      // Restore opacity on error
      const productItem = picker.closest('.make-it-a-set-product-item');
      if (productItem) {
        productItem.style.opacity = '1';
      }
    }
  }

  updatePriceOnly(productItem, selectedVariant) {
    const priceElement = productItem.querySelector('.make-it-a-set-product-item__price');
    if (priceElement && selectedVariant) {
      console.log('🔧 Updating price only for variant:', selectedVariant);

      // Format price (Shopify prices are in cents)
      const price = (selectedVariant.price / 100).toFixed(2);
      let priceHTML = `$${price}`;

      // Add compare at price if exists
      if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
        const comparePrice = (selectedVariant.compare_at_price / 100).toFixed(2);
        priceHTML = `<span class="price--on-sale">$${price}</span> <s class="price--compare">$${comparePrice}</s>`;
      }

      priceElement.innerHTML = priceHTML;
      console.log('🔧 Updated price to:', priceHTML);

      // Visual price update feedback
      priceElement.style.color = '#c41e3a';
      setTimeout(() => {
        priceElement.style.color = '';
      }, 300);
    }
  }

  async fetchAndUpdateProductUnified(picker, selectedInput, targetProductUrl) {
    try {
      console.log('🎯 UNIFIED UPDATE: Starting section rendering process');
      console.log('🎯 Target product URL:', targetProductUrl);

      const productItem = picker.closest('.make-it-a-set-product-item');
      if (!productItem) {
        console.error('No product item found for picker');
        return;
      }

      // Show loading state
      productItem.style.opacity = '0.7';
      console.log('🎯 Set loading state for product item');


      // Get selected variant information first
      const jsonUrl = targetProductUrl + '.js';
      const jsonResponse = await fetch(jsonUrl);
      if (!jsonResponse.ok) {
        throw new Error(`HTTP ${jsonResponse.status}: ${jsonResponse.statusText}`);
      }

      const productData = await jsonResponse.json();
      const selectedVariant = this.findSelectedVariantFromAllInputs(picker, productData);
      console.log('🎯 Selected variant for section rendering:', selectedVariant);

      // Use section rendering API to get fresh HTML
      const sectionId = this.getAttribute('id').replace('MakeItASetDrawer-', '');
      const sectionUrl = `${targetProductUrl}?sections=${sectionId}`;

      console.log('🎯 Fetching section rendering from:', sectionUrl);

      const sectionResponse = await fetch(sectionUrl);
      if (!sectionResponse.ok) {
        throw new Error(`HTTP ${sectionResponse.status}: ${sectionResponse.statusText}`);
      }

      const sectionsData = await sectionResponse.json();
      const sectionHTML = sectionsData[sectionId];

      if (!sectionHTML) {
        throw new Error('No section HTML returned from section rendering API');
      }

      console.log('🎯 Received section HTML, parsing and updating');

      // Parse the HTML and extract the new product item
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sectionHTML;

      const newProductItem = tempDiv.querySelector('.make-it-a-set-product-item');
      if (!newProductItem) {
        throw new Error('No product item found in section rendered HTML');
      }

      // Check if the original product item was marked as current and preserve this state
      const wasCurrentlyViewing = productItem.classList.contains('make-it-a-set-product-item--current');

      // Also check if this product item is in a complementary or related section
      const isInComplementarySection = productItem.closest('.make-it-a-set-complementary-section');
      const isInRelatedSection = productItem.closest('.make-it-a-set-related-section');

      // Remove the "Currently Viewing" label if it's not the original currently viewing product
      // or if it's in complementary/related sections
      if (!wasCurrentlyViewing || isInComplementarySection || isInRelatedSection) {
        const currentLabel = newProductItem.querySelector('.make-it-a-set-product-item__current-label');
        if (currentLabel) {
          currentLabel.remove();
          console.log('🎯 Removed incorrect "Currently Viewing" label from complementary/related product');
        }

        // Also remove the current class if it was incorrectly added
        newProductItem.classList.remove('make-it-a-set-product-item--current');
      }

      // Replace the existing product item with the new one
      productItem.replaceWith(newProductItem);
      console.log('🎯 Replaced product item with section rendered content');

      // Update the picker reference since the DOM changed
      const newPicker = newProductItem.querySelector('variant-picker');
      if (newPicker) {
        newPicker.setAttribute('data-product-url', targetProductUrl);
        console.log('🎯 Updated new picker attributes');

      }

      // Update total price
      this.updateTotalPrice();

      console.log('🎯 Section rendering complete');

    } catch (error) {
      console.error('🎯 Failed to fetch and update with section rendering:', error);
      console.log('🎯 Falling back to manual update');

      // Fallback to manual update if section rendering fails
      await this.fallbackManualUpdate(picker, selectedInput, targetProductUrl);
    }
  }

  async fallbackManualUpdate(picker, selectedInput, targetProductUrl) {
    try {
      const productItem = picker.closest('.make-it-a-set-product-item');
      if (!productItem) return;

      // Fetch product JSON data directly
      const jsonUrl = targetProductUrl + '.js';
      const response = await fetch(jsonUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const productData = await response.json();
      console.log('🔄 Fallback: Received product data:', productData);

      // Find the correct variant based on the selected input
      const selectedVariant = this.findSelectedVariantFromAllInputs(picker, productData);
      if (selectedVariant) {
        productData.selected_or_first_available_variant = selectedVariant;
        console.log('🔄 Fallback: Selected variant:', selectedVariant);
      }

      // Update the product item with new data
      console.log('🔄 Fallback: Updating product item manually');
      await this.updateProductItemWithNewData(picker, productData, targetProductUrl);

      // Restore opacity
      productItem.style.opacity = '1';
      console.log('🔄 Fallback: Manual update complete');

    } catch (error) {
      console.error('🔄 Fallback manual update also failed:', error);

      // Restore opacity on error
      const productItem = picker.closest('.make-it-a-set-product-item');
      if (productItem) {
        productItem.style.opacity = '1';
      }
    }
  }


  async fetchAndUpdateVariantWithSectionRendering(picker, selectedInput) {
    try {
      console.log('🎨 VARIANT CHANGE: Using section rendering for variant change');

      const productItem = picker.closest('.make-it-a-set-product-item');
      if (!productItem) return;

      // Show loading state
      productItem.style.opacity = '0.7';

      const currentProductUrl = picker.getAttribute('data-product-url');
      if (!currentProductUrl) {
        console.error('No product URL found on picker');
        return;
      }

      // Get the product JSON to find the correct variant
      const jsonUrl = currentProductUrl + '.js';
      console.log('🎨 VARIANT CHANGE: Fetching product JSON for variant change from:', jsonUrl);

      const response = await fetch(jsonUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const productData = await response.json();
      console.log('🎨 VARIANT CHANGE: Received product data for variant change:', productData);

      // Find the selected variant based on all current selections
      const selectedVariant = this.findSelectedVariantFromAllInputs(picker, productData);
      if (selectedVariant) {
        productData.selected_or_first_available_variant = selectedVariant;
        console.log('🎨 VARIANT CHANGE: Selected variant:', selectedVariant);
      }

      // Update the product item with new variant data
      console.log('🎨 VARIANT CHANGE: Updating product item with variant data');
      await this.updateProductItemWithNewData(picker, productData, currentProductUrl);

      // Restore opacity
      productItem.style.opacity = '1';

      // Update total price
      this.updateTotalPrice();

    } catch (error) {
      console.error('Failed to fetch and update variant with section rendering:', error);

      // Restore opacity on error
      const productItem = picker.closest('.make-it-a-set-product-item');
      if (productItem) {
        productItem.style.opacity = '1';
      }
    }
  }

  findSelectedVariantFromAllInputs(picker, productData) {
    // Get all selected option values from the variant picker
    const selectedOptions = {};
    const radioInputs = picker.querySelectorAll('input[type="radio"]:checked');

    radioInputs.forEach(input => {
      // Extract option name from input name (format: "Color-drawer-123456")
      const nameParts = input.name.split('-');
      const optionName = nameParts[0];
      selectedOptions[optionName] = input.value;
    });

    // console.log('All selected options for variant matching:', selectedOptions);

    // Find variant that matches all selected options
    const matchingVariant = productData.variants.find(variant => {
      return variant.options.every((optionValue, index) => {
        const optionName = productData.options[index].name;
        const matches = selectedOptions[optionName] === optionValue;
        // console.log(`Checking ${optionName}: ${selectedOptions[optionName]} === ${optionValue} = ${matches}`);
        return matches;
      });
    });

    // console.log('Found matching variant from all inputs:', matchingVariant);
    return matchingVariant || productData.variants[0];
  }

  async updateProductImageForVariantChange(picker, selectedInput) {
    const productItem = picker.closest('.make-it-a-set-product-item');
    if (!productItem) return;

    // For same-product variant changes, we need to fetch the full product data
    // to get the variant's featured media, similar to how the main variant picker works
    const currentProductUrl = picker.getAttribute('data-product-url');
    if (!currentProductUrl) return;

    try {
      // Show loading state
      productItem.style.opacity = '0.7';

      // Build URL with selected options to get updated variant data
      const selectedOptionValueIds = this.getSelectedOptionValueIds(picker);
      const params = new URLSearchParams();

      if (selectedOptionValueIds.length > 0) {
        params.append('option_values', selectedOptionValueIds.join(','));
      }

      const requestUrl = `${currentProductUrl}?${params.toString()}`;
      // console.log('Fetching variant data from:', requestUrl);

      const response = await fetch(requestUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Extract product data from the response
      const newVariantPicker = html.querySelector('variant-picker');
      if (!newVariantPicker) {
        throw new Error('No variant picker found in response');
      }

      const productScript = newVariantPicker.querySelector('script[type="application/json"]');
      if (!productScript) {
        throw new Error('No product JSON found in response');
      }

      const selectedVariant = JSON.parse(productScript.textContent);
      // console.log('Received variant data:', selectedVariant);

      // Update the image if the variant has featured media
      if (selectedVariant && selectedVariant.featured_media) {
        const imageElement = productItem.querySelector('.make-it-a-set-product-item__image img');
        if (imageElement) {
          const targetMedia = selectedVariant.featured_media;
          const previewImage = targetMedia.preview_image || targetMedia;

          if (previewImage && previewImage.src) {
            // Create responsive image URL
            let imageUrl = previewImage.src;

            // Add width parameter for consistent sizing
            if (!imageUrl.includes('width=')) {
              // Remove any existing size parameters and add width=600
              imageUrl = imageUrl.replace(/_\d+x\d*\.(jpg|jpeg|png|webp|gif)/i, '.$1');
              imageUrl = imageUrl + (imageUrl.includes('?') ? '&' : '?') + 'width=600';
            }

            // Update the image
            imageElement.src = imageUrl;
            imageElement.alt = targetMedia.alt || selectedVariant.title;

            // Add media ID for tracking
            const mediaContainer = imageElement.closest('.make-it-a-set-product-item__image');
            if (mediaContainer) {
              mediaContainer.setAttribute('data-media-id', targetMedia.id || '');
            }

            // console.log('Updated product image for variant change:', selectedVariant.title);
          }
        }

        // Update the picker's script with new variant data
        const currentPickerScript = picker.querySelector('script[type="application/json"]');
        if (currentPickerScript) {
          currentPickerScript.textContent = JSON.stringify(selectedVariant);
        }
      }

      // Restore opacity
      productItem.style.opacity = '1';

    } catch (error) {
      console.error('Failed to update product image for variant change:', error);
      // Restore opacity on error
      productItem.style.opacity = '1';
    }
  }

  async fetchAndUpdateProduct(picker, selectedInput, productUrl) {
    try {
      // console.log('📦 PRODUCT SWITCH: Fetching new product data');

      // Show loading state
      const productItem = picker.closest('.make-it-a-set-product-item');
      if (productItem) {
        productItem.style.opacity = '0.7';
        // console.log('Set loading state for product item');
      }

      // Fetch product JSON data directly
      const jsonUrl = productUrl + '.js';
      // console.log('📦 PRODUCT SWITCH: Fetching product JSON from:', jsonUrl);

      const response = await fetch(jsonUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const productData = await response.json();
      // console.log('📦 PRODUCT SWITCH: Received product data:', productData);

      // Get selected variant based on the option that was clicked
      const selectedVariant = this.findSelectedVariant(productData, selectedInput);
      if (selectedVariant) {
        productData.selected_or_first_available_variant = selectedVariant;
        // console.log('📦 PRODUCT SWITCH: Selected variant:', selectedVariant);
      }

      // Update the product item with new data
      // console.log('📦 PRODUCT SWITCH: Updating product item with new data');
      await this.updateProductItemWithNewData(picker, productData, productUrl);

      // Restore opacity
      if (productItem) {
        productItem.style.opacity = '1';
      }

      // Update total price
      this.updateTotalPrice();

    } catch (error) {
      console.error('Failed to fetch and update product:', error);

      // Restore opacity on error
      const productItem = picker.closest('.make-it-a-set-product-item');
      if (productItem) {
        productItem.style.opacity = '1';
      }
    }
  }

  findSelectedVariant(productData, selectedInput) {
    // For product switches, we want to find the variant that matches the selected option value
    const selectedValue = selectedInput.value;
    // console.log('Finding variant for selected value:', selectedValue);
    // console.log('Available variants:', productData.variants);

    // Find variant that has this option value in any of its options
    const matchingVariant = productData.variants.find(variant => {
      const hasMatch = variant.options.includes(selectedValue);
      // console.log(`Variant ${variant.id} options:`, variant.options, 'matches:', hasMatch);
      return hasMatch;
    });

    const finalVariant = matchingVariant || productData.variants[0];
    // console.log('Selected variant:', finalVariant);
    return finalVariant;
  }

  getSelectedOptionValueIds(picker) {
    const checkedInputs = picker.querySelectorAll('input[type="radio"]:checked');
    return Array.from(checkedInputs)
      .map(input => input.dataset.optionValueId)
      .filter(Boolean);
  }

  async updateProductItemWithNewData(picker, productData, newProductUrl) {
    // console.log('updateProductItemWithNewData called with:', {
    //   picker: picker,
    //   pickerType: typeof picker,
    //   productData: productData,
    //   productId: productData.id,
    //   productTitle: productData.title
    // });

    const productItem = picker.closest('.make-it-a-set-product-item');
    if (!productItem) {
      console.error('No product item found for picker');
      return;
    }

    // console.log('Found product item:', productItem);

    // Update product attributes
    if (picker && typeof picker.setAttribute === 'function') {
      picker.setAttribute('data-product-id', productData.id);
      picker.setAttribute('data-product-url', newProductUrl);
      // console.log('Updated picker attributes');
    } else {
      console.error('Cannot update picker attributes - invalid picker:', picker);
    }

    // Process product title and collection label (matching the Liquid logic)
    let productTitle = productData.title;
    let collectionLabel = '';

    // console.log('Processing title:', productTitle);
    // console.log('Product metafields:', productData.metafields);

    // Extract title before " | " if it exists
    if (productTitle.includes(' | ')) {
      productTitle = productTitle.split(' | ')[0];
      // console.log('Title after | split:', productTitle);
    }

    // Get collection label from metafields - NEVER use vendor as fallback
    if (productData.metafields && productData.metafields.custom && productData.metafields.custom.collection_label) {
      collectionLabel = productData.metafields.custom.collection_label;
      // console.log('Found collection label from metafields:', collectionLabel);

      // Remove collection label from title if it exists at the beginning
      if (productTitle.startsWith(collectionLabel + ' ')) {
        productTitle = productTitle.substring(collectionLabel.length + 1);
        // console.log('Removed collection label with space:', productTitle);
      } else if (productTitle.startsWith(collectionLabel)) {
        productTitle = productTitle.substring(collectionLabel.length).trim();
        // console.log('Removed collection label without space:', productTitle);
      }
    } else {
      // Try to infer collection label from title pattern
      // If title is "Vintage Fleece Short", assume "Vintage Fleece" is collection label
      const titleWords = productTitle.split(' ');
      if (titleWords.length >= 3) {
        // Take all words except the last one as potential collection label
        const potentialLabel = titleWords.slice(0, -1).join(' ');
        collectionLabel = potentialLabel;
        productTitle = titleWords[titleWords.length - 1]; // Just the last word
        // console.log('Inferred collection label:', collectionLabel, 'title:', productTitle);
      } else {
        console.log('No collection label found, keeping full title');
      }
    }

    // console.log('Final collection label:', collectionLabel);
    // console.log('Final product title:', productTitle);

    // Update product title
    const titleElement = productItem.querySelector('.make-it-a-set-product-item__title');
    if (titleElement) {
      titleElement.textContent = productTitle;
    }

    // Update brand/collection label
    const brandElement = productItem.querySelector('.make-it-a-set-product-item__brand');
    if (brandElement) {
      if (collectionLabel) {
        brandElement.textContent = collectionLabel;
        brandElement.style.display = '';
      } else {
        brandElement.style.display = 'none';
      }
    }

    // Update product image using selected variant's featured media (like product media gallery)
    const selectedVariant = productData.selected_or_first_available_variant ||
                           (productData.variants && productData.variants[0]);

    const imageElement = productItem.querySelector('.make-it-a-set-product-item__image img');
    if (imageElement) {
      // console.log('Updating product image. Selected variant:', selectedVariant);
      // console.log('Product images available:', productData.images);

      let imageUrl = '';

      // Always use the LAST image from the product images array (as requested)
      if (productData.images && productData.images.length > 0) {
        // Get the last image from the array
        const lastImageIndex = productData.images.length - 1;
        imageUrl = productData.images[lastImageIndex];
        // console.log('✅ Using last product image (index ' + lastImageIndex + '):', imageUrl);
        // console.log('All available images:', productData.images);
      }
      // Fallback to variant featured image if no images array
      else if (selectedVariant && selectedVariant.featured_image && selectedVariant.featured_image.src) {
        imageUrl = selectedVariant.featured_image.src;
        // console.log('✅ Fallback to variant featured image:', imageUrl);
      }
      // Final fallback to featured image
      else if (productData.featured_image) {
        imageUrl = productData.featured_image;
        // console.log('✅ Fallback to product featured image:', imageUrl);
      }
      else {
        console.error('❌ No image found in any of the sources');
        // console.log('Product data structure:', {
        //   images: productData.images,
        //   featured_image: productData.featured_image,
        //   variant: selectedVariant
        // });
      }

      if (imageUrl) {
        // Add width parameter for consistent sizing
        if (!imageUrl.includes('width=')) {
          // Remove any existing size parameters and add width=600
          imageUrl = imageUrl.replace(/_\d+x\d*\.(jpg|jpeg|png|webp|gif)/i, '.$1');
          imageUrl = imageUrl + (imageUrl.includes('?') ? '&' : '?') + 'width=600';
        }

        // Update the image with smooth transition
        imageElement.style.opacity = '0.7';
        imageElement.src = imageUrl;
        imageElement.alt = selectedVariant ? selectedVariant.name : productData.title;

        console.log('Updated image src to:', imageUrl);

        // Restore opacity once loaded
        imageElement.onload = () => {
          imageElement.style.opacity = '1';
          imageElement.onload = null;
          console.log('Image loaded successfully');
        };

        // Handle load error
        imageElement.onerror = () => {
          console.error('Failed to load image:', imageUrl);
          imageElement.style.opacity = '1';
          imageElement.onerror = null;
        };
      }
    }

    // Update price with selected variant
    if (selectedVariant) {
      console.log('Updating price with variant:', selectedVariant);
      const priceElement = productItem.querySelector('.make-it-a-set-product-item__price');
      if (priceElement) {
        // Format price (Shopify prices are in cents)
        const price = (selectedVariant.price / 100).toFixed(2);
        let priceHTML = `$${price}`;

        // Add compare at price if exists
        if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
          const comparePrice = (selectedVariant.compare_at_price / 100).toFixed(2);
          priceHTML = `<span class="price--on-sale">$${price}</span> <s class="price--compare">$${comparePrice}</s>`;
        }

        priceElement.innerHTML = priceHTML;
        console.log('Updated price to:', priceHTML);

        // Visual price update feedback
        priceElement.style.color = '#c41e3a';
        setTimeout(() => {
          priceElement.style.color = '';
        }, 500);
      }
    }

    // Update variant picker data
    if (picker && typeof picker.querySelector === 'function') {
      const pickerScript = picker.querySelector('script[type="application/json"]');
      if (pickerScript) {
        pickerScript.textContent = JSON.stringify(selectedVariant);
      }

      // Update the hidden variant ID input
      const variantIdInput = productItem.querySelector('.variant-id-input');
      if (variantIdInput && selectedVariant) {
        variantIdInput.value = selectedVariant.id;
        console.log('Updated variant ID input to:', selectedVariant.id);
      }
    } else {
      console.error('Picker element is not valid:', picker);
    }

    // Update variant picker options availability
    await this.updateVariantPickerOptions(picker, productData);

    // Update total pricing to maintain discounts after color changes
    this.updateTotalPrice();

    console.log('Product item updated successfully');
  }

  async updateVariantPickerOptions(picker, productData) {
    console.log('Updating variant picker options for new product');

    // Update all variant option inputs with new availability
    const variantInputs = picker.querySelectorAll('input[type="radio"]');

    variantInputs.forEach(input => {
      const optionValue = input.value;
      console.log('Checking availability for option:', optionValue);

      // Find if this option value is available in any variant
      const isAvailable = productData.variants.some(variant => {
        const hasOption = variant.options.includes(optionValue);
        const isVariantAvailable = variant.available;
        console.log(`Variant ${variant.id} has ${optionValue}:`, hasOption, 'available:', isVariantAvailable);
        return hasOption && isVariantAvailable;
      });

      console.log(`Option ${optionValue} is available:`, isAvailable);

      // Update the input's availability
      if (isAvailable) {
        input.removeAttribute('aria-disabled');
        input.setAttribute('data-option-available', 'true');
        input.disabled = false;
      } else {
        input.setAttribute('aria-disabled', 'true');
        input.setAttribute('data-option-available', 'false');
        input.disabled = true;
      }

      // Update parent label styling
      const label = input.closest('label');
      if (label) {
        if (isAvailable) {
          label.classList.remove('variant-option--unavailable');
        } else {
          label.classList.add('variant-option--unavailable');
        }
      }
    });

    console.log('Variant picker options updated');
  }

  getSelectedVariants() {
    const variants = [];

    // Get all active product items
    const activeItems = this.querySelectorAll('.make-it-a-set-product-item');
    console.log('🔍 Found product items:', activeItems.length);

    activeItems.forEach((item, index) => {
      console.log(`🔍 Checking item ${index + 1}:`, item);

      const carousel = item.closest('.make-it-a-set-product-carousel');

      // Skip if this item is in a carousel but not the active slide
      if (carousel) {
        const activeSlide = carousel.querySelector('.make-it-a-set-product-carousel__slide--active .make-it-a-set-product-item');
        if (activeSlide !== item) {
          console.log(`🔍 Item ${index + 1} not active slide, skipping`);
          return;
        }
      }

      // Skip if this item is in a hidden section
      const section = item.closest('.make-it-a-set-product-section');
      if (section && section.hasAttribute('hidden')) {
        console.log(`🔍 Item ${index + 1} in hidden section, skipping`);
        return;
      }

      // Get the hidden variant ID input (simpler approach)
      const variantIdInput = item.querySelector('.variant-id-input');
      if (!variantIdInput) {
        console.log(`🔍 Item ${index + 1} no variant ID input found`);
        return;
      }

      const variantId = variantIdInput.value;
      if (!variantId) {
        console.log(`🔍 Item ${index + 1} no variant ID value`);
        return;
      }

      // Get variant data from the script
      const variantPicker = item.querySelector('variant-picker');
      if (!variantPicker) {
        console.log(`🔍 Item ${index + 1} no variant picker found`);
        return;
      }

      const productScript = variantPicker.querySelector('script[type="application/json"]');
      if (!productScript) {
        console.log(`🔍 Item ${index + 1} no product script found`);
        return;
      }

      try {
        const variantData = JSON.parse(productScript.textContent.trim());
        
        // Verify the variant ID matches
        if (variantData.id && variantData.id.toString() === variantId.toString()) {
          // Tag-based Final Sale trigger (final-sale-active), read from the
          // variant-picker's data attribute since it isn't part of the raw
          // Shopify variant JSON above. See variant-main-picker.liquid.
          variantData._finalSaleTag = variantPicker.dataset.finalSale === 'true';
          variants.push(variantData);
          console.log(`🔍 Item ${index + 1} variant added to cart list:`, {
            id: variantData.id,
            price: variantData.price,
            title: variantData.title
          });
        } else {
          console.log(`🔍 Item ${index + 1} variant ID mismatch: script=${variantData.id}, input=${variantId}`);
        }
      } catch (e) {
        console.error(`Failed to parse variant data for item ${index + 1}:`, e);
      }
    });

    console.log('🔍 Final selected variants:', variants.map(v => ({ id: v.id, price: v.price, title: v.title })));
    return variants;
  }

  findSelectedVariant(picker, productData) {
    console.log('🔍 findSelectedVariant called with:', { picker, productData });

    const form = picker.querySelector('form');
    if (!form) {
      console.log('🔍 No form found, using fallback variant');
      return productData.selected_or_first_available_variant || productData.variants?.[0] || null;
    }

    if (!productData.variants || productData.variants.length === 0) {
      console.log('🔍 No variants available');
      return null;
    }

    // If only one variant, return it
    if (productData.variants.length === 1) {
      console.log('🔍 Only one variant available:', productData.variants[0]);
      return productData.variants[0];
    }

    const formData = new FormData(form);
    const selectedOptions = {};

    console.log('🔍 Form data entries:');
    for (let [key, value] of formData.entries()) {
      console.log(`🔍   ${key}: ${value}`);
      // Extract option name (remove product ID suffix)
      const optionName = key.replace(/-drawer-\d+$/, '');
      selectedOptions[optionName] = value;
    }

    console.log('🔍 Selected options after processing:', selectedOptions);
    console.log('🔍 Product options:', productData.options);

    // Find matching variant
    const variant = productData.variants.find(v => {
      if (!v.options || !productData.options) {
        console.log('🔍 Variant missing options data:', v);
        return false;
      }

      const matches = Object.entries(selectedOptions).every(([optionKey, optionValue]) => {
        // Find option index by name (handle both string and object formats)
        let optionIndex = -1;
        if (Array.isArray(productData.options)) {
          optionIndex = productData.options.findIndex(opt => {
            return (typeof opt === 'string' ? opt : opt.name) === optionKey;
          });
        }

        const variantOption = v.options[optionIndex];
        const matches = optionIndex !== -1 && variantOption === optionValue;

        console.log(`🔍   Checking ${optionKey}[${optionIndex}]: ${optionValue} === ${variantOption} = ${matches}`);
        return matches;
      });

      console.log(`🔍 Variant ${v.id} matches:`, matches);
      return matches;
    });

    const finalVariant = variant || productData.selected_or_first_available_variant || productData.variants[0];
    console.log('🔍 Final selected variant:', finalVariant);
    return finalVariant;
  }

  findSelectedVariantFromPicker(picker, productData) {
    console.log('🔍 findSelectedVariantFromPicker called');

    // The script contains a single variant object, not a product with variants array
    if (productData.id && productData.price !== undefined) {
      console.log('🔍 Product data is a single variant:', productData);
      return productData;
    }

    console.log('🔍 Product data structure:', {
      hasVariants: !!productData.variants,
      variantCount: productData.variants?.length,
      options: productData.options,
      firstVariant: productData.variants?.[0],
      isVariantObject: !!(productData.id && productData.price !== undefined)
    });

    if (!productData.variants || productData.variants.length === 0) {
      console.log('🔍 No variants available');
      return null;
    }

    // If only one variant, return it
    if (productData.variants.length === 1) {
      console.log('🔍 Only one variant available:', productData.variants[0]);
      return productData.variants[0];
    }

    // Get all checked radio inputs from the picker
    const checkedInputs = picker.querySelectorAll('input[type="radio"]:checked');
    console.log('🔍 Found checked inputs:', checkedInputs.length);

    // If no inputs are checked, try to find default selections or return first variant
    if (checkedInputs.length === 0) {
      console.log('🔍 No options selected, trying to find defaults or first variant');

      // Try to return the variant from the script (which should be the default selected variant)
      const defaultVariant = productData.selected_or_first_available_variant || productData.variants[0];
      console.log('🔍 Using default variant:', defaultVariant);
      return defaultVariant;
    }

    const selectedOptions = {};
    checkedInputs.forEach(input => {
      // Extract option name from input name: "Color-drawer-123456" -> "Color"
      const optionName = input.name.replace(/-drawer-\d+$/, '');
      selectedOptions[optionName] = input.value;
      console.log(`🔍 Selected option: ${optionName} = ${input.value}`);
    });

    console.log('🔍 All selected options:', selectedOptions);
    console.log('🔍 Product options:', productData.options);

    // Find matching variant based on selected options
    const matchingVariant = productData.variants.find(variant => {
      console.log(`🔍 Checking variant ${variant.id}:`, variant);

      if (!variant.options) {
        console.log('🔍   No options on variant');
        return false;
      }

      // Need to match ALL selected options
      const optionNames = Object.keys(selectedOptions);
      if (optionNames.length === 0) {
        console.log('🔍   No selected options to match');
        return false;
      }

      const matches = optionNames.every(optionName => {
        // Find the index of this option in the product's options array
        const optionIndex = productData.options.findIndex(opt => opt === optionName);
        if (optionIndex === -1) {
          console.log(`🔍   Option ${optionName} not found in product options`);
          return false;
        }

        const variantOptionValue = variant.options[optionIndex];
        const selectedValue = selectedOptions[optionName];
        const isMatch = selectedValue === variantOptionValue;

        console.log(`🔍   Option ${optionName}[${optionIndex}]: variant="${variantOptionValue}" selected="${selectedValue}" match=${isMatch}`);
        return isMatch;
      });

      console.log(`🔍 Variant ${variant.id} overall match: ${matches}`);
      return matches;
    });

    const finalVariant = matchingVariant || productData.selected_or_first_available_variant || productData.variants[0];
    console.log('🔍 Final selected variant from picker:', finalVariant);
    console.log('🔍 Final variant price (should be in cents):', finalVariant?.price);
    return finalVariant;
  }

  updateTotalPrice() {
    console.log('💰 Starting updateTotalPrice...');
    const variants = this.getSelectedVariants();
    const totalPriceElement = this.querySelector('.total-price');
    const addToCartBtn = this.querySelector('.cart__checkout-button');

    console.log('💰 Selected variants:', variants);
    console.log('💰 Total price element:', totalPriceElement);
    console.log('💰 Add to cart button:', addToCartBtn);

    let totalPrice = 0;
    let hasAllVariants = variants.length > 0;
    let itemCount = 0;

    // Calculate total price and count items
    variants.forEach((variant, index) => {
      console.log(`💰 Processing variant ${index + 1}:`, variant);

      if (variant && variant.price !== undefined && variant.price !== null) {
        // Ensure price is a number (Shopify prices are in cents)
        const price = typeof variant.price === 'string' ? parseInt(variant.price, 10) : variant.price;

        if (!isNaN(price) && price > 0) {
          totalPrice += price;
          itemCount++;
          console.log(`💰   Added price: ${price} cents (total now: ${totalPrice})`);
        } else {
          console.log(`💰   Invalid price: ${variant.price} (type: ${typeof variant.price})`);
          hasAllVariants = false;
        }
      } else {
        console.log(`💰   No price on variant: ${variant?.id || 'unknown'}`);
        hasAllVariants = false;
      }
    });

    // Determine discount percentage
    const relatedSectionExists = this.querySelector('.make-it-a-set-related-section');
    const discountPercentage = relatedSectionExists ? 15 : 10;

    // Apply discount for 2+ items
    let discountedTotal = totalPrice;
    let discountAmount = 0;

    if (itemCount > 1) {
      discountAmount = totalPrice * (discountPercentage / 100);
      discountedTotal = totalPrice - discountAmount;
    }

    // Format prices (Shopify prices are in cents)
    const formattedOriginalPrice = (totalPrice / 100).toFixed(2);
    const formattedDiscountedPrice = (discountedTotal / 100).toFixed(2);

    // Update the total price span
    if (totalPriceElement) {
      if (itemCount > 1) {
        totalPriceElement.innerHTML = `
          <span class="compare-at-price">$${formattedOriginalPrice}</span>
          <span class="total-price price">$${formattedDiscountedPrice}</span>
        `;
      } else {
        totalPriceElement.innerHTML = `<span class="total-price price">${itemCount > 0 ? `$${formattedOriginalPrice}` : '$0.00'}</span>`;
      }
    }

    // Update button text and state
    if (addToCartBtn) {
      const buttonContent = addToCartBtn.innerHTML;

      // Only update if not in loading state
      if (!buttonContent.includes('Adding') && !buttonContent.includes('Added')) {
        if (itemCount > 1) {
          const priceHTML = totalPriceElement ? totalPriceElement.innerHTML : `<span class="price">$${formattedDiscountedPrice}</span>`;
          addToCartBtn.innerHTML = `Add Set to Cart (${itemCount} items) — ${priceHTML}`;
        } else if (itemCount === 1) {
          addToCartBtn.innerHTML = `Add to Cart — <span class="price">$${formattedOriginalPrice}</span>`;
        } else {
          addToCartBtn.innerHTML = `Add to Cart — <span class="price">$0.00</span>`;
        }
      }

      addToCartBtn.disabled = !hasAllVariants || variants.length === 0;
    }

    console.log(`💰 Price calculation: ${itemCount} items, ${discountPercentage}% discount, original: $${formattedOriginalPrice}, final: $${formattedDiscountedPrice}`);

    // Update individual product prices to show discounts
    this.updateIndividualProductPrices(variants, discountPercentage, itemCount > 1);
  }

  updateIndividualProductPrices(variants, discountPercentage, showDiscount) {
    console.log('💰 Updating individual product prices...');

    // Get all active product items
    const activeItems = this.querySelectorAll('.make-it-a-set-product-item');

    activeItems.forEach((item, index) => {
      const carousel = item.closest('.make-it-a-set-product-carousel');

      // Skip if this item is in a carousel but not the active slide
      if (carousel) {
        const activeSlide = carousel.querySelector('.make-it-a-set-product-carousel__slide--active .make-it-a-set-product-item');
        if (activeSlide !== item) return;
      }

      // Skip if this item is in a hidden section
      const section = item.closest('.make-it-a-set-product-section');
      if (section && section.hasAttribute('hidden')) return;

      const priceElement = item.querySelector('.make-it-a-set-product-item__price');
      if (!priceElement) return;

      // Find the corresponding variant for this item
      const variantPicker = item.querySelector('variant-picker');
      if (!variantPicker) return;

      const productScript = variantPicker.querySelector('script[type="application/json"]');
      if (!productScript) return;

      try {
        const productData = JSON.parse(productScript.textContent);
        const selectedVariant = this.findSelectedVariantFromPicker(variantPicker, productData);

        console.log('💰 Individual price update - productData:', {
          id: productData.id,
          price: productData.price,
          isVariant: !!(productData.id && productData.price !== undefined)
        });
        console.log('💰 Individual price update - selectedVariant:', selectedVariant);

        // Use the selected variant or fallback to productData if it's a variant object
        const variantToUse = selectedVariant || (productData.id && productData.price !== undefined ? productData : null);

        if (variantToUse && variantToUse.price !== undefined && variantToUse.price !== null) {
          const originalPrice = variantToUse.price;
          const compareAtPrice = variantToUse.compare_at_price;

          if (showDiscount) {
            // Calculate discounted price
            const discountAmount = originalPrice * (discountPercentage / 100);
            const discountedPrice = originalPrice - discountAmount;

            // Format prices
            const formattedOriginal = (originalPrice / 100).toFixed(2);
            const formattedDiscounted = (discountedPrice / 100).toFixed(2);

            if (compareAtPrice && compareAtPrice > originalPrice) {
              // Product is already on sale, show: [compare_at] [original] [discounted]
              const formattedCompareAt = (compareAtPrice / 100).toFixed(2);
              priceElement.innerHTML = `
                <span class="compare-at-price">$${formattedCompareAt}</span>
                <span class="compare-at-price">$${formattedOriginal}</span>
                <span class="price">$${formattedDiscounted}</span>
              `;
            } else {
              // Regular product with set discount: [original] [discounted]
              priceElement.innerHTML = `
                <span class="compare-at-price">$${formattedOriginal}</span>
                <span class="price">$${formattedDiscounted}</span>
              `;
            }
          } else {
            // No set discount, show normal pricing
            if (compareAtPrice && compareAtPrice > originalPrice) {
              // Product is on sale: [compare_at] [sale_price]
              const formattedCompareAt = (compareAtPrice / 100).toFixed(2);
              const formattedSale = (originalPrice / 100).toFixed(2);
              priceElement.innerHTML = `
                <span class="compare-at-price">$${formattedCompareAt}</span>
                <span class="price">$${formattedSale}</span>
              `;
            } else {
              // Regular pricing
              const formattedPrice = (originalPrice / 100).toFixed(2);
              priceElement.innerHTML = `<span class="price">$${formattedPrice}</span>`;
            }
          }
        }
      } catch (e) {
        console.error('Failed to update individual price:', e);
      }
    });
  }

  async addToCart(variantId, quantity = 1) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to add variant ${variantId} to cart`);
    }

    return response.json();
  }

  async bulkAddToCart(items) {
    console.log('🛒 Adding items to cart:', items);

    try {
      // Use the standard cart/add.js endpoint for bulk additions
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          items: items
        })
      });

      console.log('🛒 Cart API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🛒 Cart API error:', errorText);
        throw new Error(`Failed to add items to cart: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('🛒 Cart API success:', result);
      return result;

    } catch (error) {
      console.error('🛒 Bulk add to cart failed:', error);
      throw error;
    }
  }

  async applyDiscountCode(discountCode) {
    try {
      console.log(`🎫 Applying discount code: ${discountCode}`);

      // Use cart note or attributes to pass discount information
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            'Discount Code': discountCode,
            'Make It A Set': 'true'
          },
          note: `Discount Code: ${discountCode}`
        })
      });

      if (updateResponse.ok) {
        console.log(`🎫 Successfully applied discount code: ${discountCode}`);

        // Dispatch custom event to notify other parts of the theme
        document.dispatchEvent(new CustomEvent('discount:applied', {
          detail: {
            code: discountCode,
            source: 'make-it-a-set-drawer'
          }
        }));
      } else {
        console.warn(`🎫 Could not apply discount code: ${updateResponse.status}`);
      }
    } catch (error) {
      console.warn('🎫 Failed to apply discount code:', error);
      // Don't throw error - discount code application is not critical for cart functionality
    }
  }

  /**
   * Animates the add to cart button like the theme's standard animation
   */
  animateAddToCart(button) {
    const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000; // Match theme duration

    if (!button.classList.contains('atc-added')) {
      button.classList.add('atc-added');
    }

    // Use theme's exact pattern with nested timeouts for proper cleanup
    setTimeout(() => {
      setTimeout(() => {
        button.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Triggers the fly to cart animation like the theme's standard animation
   */
  triggerFlyToCartAnimation() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const button = this.querySelector('.cart__checkout-button');

    // Get the first product image from the drawer
    const firstProductImage = this.querySelector('.make-it-a-set-product-item__image img');

    if (!cartIcon || !button || !firstProductImage) {
      console.log('Missing elements for fly to cart animation:', {
        cartIcon: !!cartIcon,
        button: !!button,
        firstProductImage: !!firstProductImage
      });
      return;
    }

    const flyToCartElement = document.createElement('fly-to-cart');

    flyToCartElement.style.setProperty('background-image', `url(${firstProductImage.src})`);
    flyToCartElement.source = button;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  open() {
    this.showDialog();

    // Remove any previously added related section and divider
    const existingRelatedSection = this.querySelector('.make-it-a-set-related-section');
    const existingRelatedDivider = this.querySelector('.make-it-a-set-related-divider');
    const pullButton = this.querySelector('#pull-it-all-together');

    if (existingRelatedSection) {
      // Only recreate the button if the setting is enabled
      if (this.shouldShowPullTogether()) {
        let newPIT = document.createElement("div")
        
        newPIT.classList.add("make-it-a-set-pull-together")
        newPIT.style.display = "flex"
        newPIT.innerHTML = `<button type="button" class="make-it-a-set-pull-together__button" id="pull-it-all-together" style="display: block;"><span>Pull it all together</span></button>`
        
        document.querySelector(".make-it-a-set-related-section").after(newPIT)
        document.querySelector("make-it-a-set-drawer-component").initializePullTogether()
      }

      existingRelatedSection.remove();
      
      const closePitButton = document.querySelector(".close-pit");
      if (closePitButton) {
        closePitButton.classList.add("hidden")
      }
      
      this.updateTotalPrice();
    }

    if (existingRelatedDivider) {
      existingRelatedDivider.remove();
    }

    // Reset and show the pull button only if the setting is enabled
    if (pullButton && this.shouldShowPullTogether()) {
      pullButton.style.display = 'block';
      pullButton.disabled = false;
      pullButton.innerHTML = '<span>Pull it all together</span>';
    }

    // Update total price
    setTimeout(() => {
      this.updateTotalPrice();
    }, 100);
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('make-it-a-set-drawer-component')) {
  customElements.define('make-it-a-set-drawer-component', MakeItASetDrawerComponent);
}